import { Fragment, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    Typography,
    Box,
    IconButton,
    Tooltip,
    Stack
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { map, isEmpty, isArray, isNumber } from 'lodash';
import generateCloudinaryLink from 'src/_utils/generateCloudinaryLink';
import { generatePDF } from 'src/_utils/pdfUtils';
import AWB from "src/views/AWB";
import moment from "moment";
import 'moment/locale/ar';
import { ALL_FAILED_ORDER_STATUS, ORDER_STATUS_VALUES, ROLES, TASK_TYPES } from 'src/constants';
import { EstimationTimeDate } from "src/_modules/common";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Edit, FileCopyOutlined, CheckCircle } from "@mui/icons-material";
import UpdateOrderStatusDialog from "./UpdateOrderStatusDialog";
import { OrderShipmentImage } from "src/_modules/Orders/components";
import ZoneHistoryDialog from "./ZoneHistoryDialog";
import StatusHistoryDialog from "./StatusHistoryDialog";
import { useGetArchivedOrderStatusHistoryQuery, useGetOrderStatusHistoryQuery } from "src/_api/orderApi";
import CopyTrackingUrl from "./CopyTrackingUrl";
import CheckIsAwbUploaded from "src/_modules/Orders/components/CheckIsAwbUploaded";
import ShipmentFailReasonDialog from "src/_modules/Orders/components/ShipmentFailReasonDialog";
import MapitShipStatusTrackGrid from "./MapitShipStatusTrackGrid";

moment.locale(localStorage.getItem("i18nextLng"));

const OrderShipmentDetails = ({
    details = {
        currentStatus: '', orderNumber: '', createdAt: '', updatedAt: '', barCode: [], statusTrack: [], zone: {}, failReason: '', notes: ''
    },
    ...props
}) => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const { pathname } = useLocation()
    const [searchParams] = useSearchParams()
    const archived = pathname.includes("/archived/") || searchParams.get("tab") === "archived"

    const isMapitshipOrder = details?.otoIntegration?.mapItShip 

    const { profile } = useSelector((state) => state.entities.profile)

    const { currentStatus, orderNumber, createdAt, updatedAt, barCode, statusTrack, zone, failReason, notes } = details;

    const canFetchStatusHistory = profile?.role === ROLES.admin && !!details?.id

    const { data: recentStatusTrackInDetails } = useGetOrderStatusHistoryQuery(details?.id, { skip: !canFetchStatusHistory || archived });
    const { data: archivedStatusTrackInDetails } = useGetArchivedOrderStatusHistoryQuery(details?.id, { skip: !canFetchStatusHistory || !archived });

    const statusTrackInDetails = archivedStatusTrackInDetails || recentStatusTrackInDetails

    const [loadingAWB, setLoadingAWB] = useState(false);
    const handleAWBClick = _ => {
        setLoadingAWB(true);
        generatePDF(
            details.barCode,
            "awb",
            "display",
            `AWB_Order#${orderNumber}`,
            _ => setLoadingAWB(false)
        );
    }

    const [open, setOpen] = useState(false);
    const [openZoneHistoryDialog, setOpenZoneHistoryDialog] = useState(false);

    const openZoneHistoryDialogHandler = (orderId) => {
        setOpenZoneHistoryDialog(true);
    }

    const [openStatusHistoryDialog, setOpenStatusHistoryDialog] = useState(false);
    const [statusHistory, setStatusHistory] = useState(null);

    const openStatusHistoryDialogHandler = (data) => {
        setStatusHistory(data);
        setOpenStatusHistoryDialog(true);
    }

    const orderNotCompletedOrReturned = !statusTrack?.find(el => [ORDER_STATUS_VALUES.completed, ...ALL_FAILED_ORDER_STATUS, ORDER_STATUS_VALUES.assignedForReturn].includes(el?.status))
    const showEstimeatedTime = orderNotCompletedOrReturned && !!details?.arrivalTime?.to
    const showRescheduleDate = orderNotCompletedOrReturned && !!details?.deliveryDate

    const showEdit = () => {
        if (details.orderType === "RETURN") return false;

        const isStatusCompleted = [ORDER_STATUS_VALUES.completed, ...ALL_FAILED_ORDER_STATUS].includes(currentStatus);
        const isOrderCreatedByMerchantAndDeliverTypeIsInternal = profile?.merchant?.role === ROLES.basic && details?.creator === ROLES.merchant && details?.deliverType === "INTERNAL_DELIVERY"

        if (isStatusCompleted || details?.otoIntegration?.mapItShip || (profile?.role !== ROLES.admin && profile?.merchant?.shippingType === "FIRST_MILE")) return false;
        if (profile?.role === ROLES.admin || profile?.merchant?.role !== ROLES.basic || isOrderCreatedByMerchantAndDeliverTypeIsInternal) return true;

        return false;
    }

    const showAlertForNonDriverAcceptOrder = () => {
        if (profile?.merchant?.role === ROLES.merchant) {
            return details?.currentStatus === ORDER_STATUS_VALUES.created && moment().diff(moment(createdAt), 'minutes') >= 10
        }
        return false
    }

    const orderTimelineStatus = useCallback((status, index) => {
        if (details?.orderType === TASK_TYPES.returnTask) {
            if ([ORDER_STATUS_VALUES.returned].includes(status)) return t("In Merchant's warehouse")

            if ([ORDER_STATUS_VALUES.pending].includes(status) && (isNumber(index) && index !== 0)) {
                return t("In Merchant's warehouse")
            }
        }

        // if (status === ORDER_STATUS_VALUES.failed && details?.orderType === TASK_TYPES.pickup) return t("Failed pickup order")

        return t(status)
    }, [details?.orderType, t])

    const [resendAwbError, setResendAwbError] = useState(null);

    return (
        <Card>
            <ZoneHistoryDialog
                open={openZoneHistoryDialog}
                onClose={() => setOpenZoneHistoryDialog(false)}
                id={details?.id}
                type="ORDER"
            />
            <StatusHistoryDialog
                open={openStatusHistoryDialog}
                statusHistory={statusHistory}
                onClose={() => {
                    setOpenStatusHistoryDialog(false)
                    setStatusHistory(null)
                }}
            />
            <CardHeader
                title={
                    <Box display="flex" alignItems="center">
                        <Typography variant="h5" sx={{ mr: 5 }}>
                            {t("Shipment Info")}
                        </Typography>
                        {
                            details?.automaitcallyIn &&
                            < Box display="flex" alignItems="center">
                                <Typography sx={{ mr: 1 }}>
                                    {t("Automated entered to the warehouse")}
                                </Typography>
                                <CheckCircle color="success" />
                            </Box>
                        }
                        {
                            details?.manual &&
                            < Box display="flex" alignItems="center">
                                <Typography sx={{ mr: 1 }}>
                                    {t("Created manually")}
                                </Typography>
                                <CheckCircle color="success" />
                            </Box>
                        }
                    </Box>
                }
                action={
                    <Stack gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {
                                !details?.otoIntegration?.mapItShip &&
                                <LoadingButton
                                    onClick={handleAWBClick}
                                    color="main"
                                    variant="contained"
                                    disableElevation
                                    loading={loadingAWB}
                                    disabled={isEmpty(details)}
                                    sx={{ textTransform: "none", fontWeight: "bold", }}
                                >
                                    {t("Print AWB")}
                                </LoadingButton>
                            }
                            <CheckIsAwbUploaded
                                orderFrom={details?.orderFrom}
                                orderId={details?.id}
                                merchantOrderNumber={details?.merchantOrderNumber}
                                onError={(error) => {
                                    setResendAwbError(error);
                                }}
                            />
                        </Box>
                        {!!resendAwbError &&
                            <Typography color="error" variant="caption">
                                {'resendAwbError'}
                            </Typography>
                        }
                    </Stack>
                }
            >
            </CardHeader>
            <Divider />
            <CardContent>
                <Grid container spacing={2} padding={2}>
                    {
                        showRescheduleDate &&
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center">
                                <Typography color="text.lighter" variant='body2' sx={{ mr: 2 }}>{t("This order is scheduled")} : </Typography>
                                <Typography variant="h6">{moment(details?.deliveryDate).format("dddd DD/MM/YY")}</Typography>
                            </Box>
                        </Grid>
                    }
                    {
                        showAlertForNonDriverAcceptOrder() &&
                        <Grid item xs={12}>
                            <Typography color="secondary" variant='body2' sx={{ mr: 2 }}>{t("No driver accept this order yet.")}</Typography>
                        </Grid>
                    }
                    {
                        showEstimeatedTime &&
                        <Grid item xs={12}>
                            <EstimationTimeDate message="It will be delivered" from={details?.arrivalTime?.from} to={details?.arrivalTime?.to} />
                        </Grid>
                    }
                    {
                        (!!details?.proofOfDelivery && !details?.otoIntegration?.mapItShip) &&
                        <Grid item md={6} xs={12}>
                            <Box display="flex" alignItems="center">
                                <Typography color="text.lighter" sx={{ marginInlineEnd: 2 }} display='inline-block'>{t("Proof of Delivery")} : </Typography>
                                <Typography display='inline-block' sx={{ fontWeight: "bold" }}>{details?.proofOfDelivery}</Typography>
                            </Box>
                        </Grid>
                    }
                    {
                        (currentStatus === ORDER_STATUS_VALUES.completed && ["PAID_ONLINE", "PAID_CASH", "PAID_POS"].includes(details?.paymentStatus)) &&
                        <Grid item md={6} xs={12}>
                            <Box display="flex" alignItems="center">
                                <Typography color="text.lighter" sx={{ marginInlineEnd: 2 }} display='inline-block'>
                                    {t("Payment method")} :
                                </Typography>

                                <Typography display='inline-block' sx={{ fontWeight: "bold" }}>
                                    {t("orderPayment", { amount: details?.amountPayable, paymentMethod: t(`paymentMethod.${details?.paymentStatus}`) })}
                                </Typography>
                            </Box>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <Box>
                            <Typography color="text.lighter" variant='body2' sx={{ marginInlineEnd: 15 }} display='inline-block'>{t("Current Status")}</Typography>
                            <Box display="flex" alignItems="center">
                                <Typography
                                    color={
                                        currentStatus === ORDER_STATUS_VALUES.completed
                                            ?
                                            "text.success"
                                            : ALL_FAILED_ORDER_STATUS.includes(currentStatus)
                                                ? "text.danger"
                                                : null
                                    }
                                    variant="h2"
                                >
                                    {orderTimelineStatus(currentStatus)}
                                </Typography>
                                {
                                    showEdit() &&
                                    <>
                                        <UpdateOrderStatusDialog
                                            order={details}
                                            open={open}
                                            onClose={() => setOpen(false)}
                                        />
                                        <Tooltip title={t("Edit Order Status")}>
                                            <IconButton onClick={() => setOpen(true)}>
                                                <Edit color="success" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                }
                            </Box>
                        </Box>
                        <Box>
                            {
                                failReason &&
                                <Fragment>
                                    <Typography color="text.lighter" sx={{ marginInlineEnd: 4 }} display='inline-block' variant="body2">{t("Fail Reason")}:</Typography>
                                    <Typography display='inline-block' variant="body2">{failReason}</Typography>
                                </Fragment>
                            }
                            {
                                notes &&
                                <Fragment>
                                    <Typography color="text.lighter" sx={{ marginInlineEnd: 4 }} display='inline-block' variant="body2">{t("Notes")}:</Typography>
                                    <Typography display='inline-block' variant="body2">{notes}</Typography>
                                </Fragment>
                            }
                        </Box>
                    </Grid>

                    {isMapitshipOrder ? (
                        <>
                            {/* MapIt Ship layout: order info in a horizontal row, then full-width DataGrid */}
                            <Grid item xs={6} sm={3}>
                                <Typography color="text.lighter" variant='body2'>
                                    {`${t("Order No.")} `}
                                </Typography>
                                <Typography variant='h3'>
                                    {orderNumber}
                                </Typography>
                            </Grid>
                            {!!details?.task?.taskNumber &&
                                <Grid item xs={6} sm={3}>
                                    <Typography color="text.lighter" variant='body2'>
                                        {`${t("Task No.")} `}
                                    </Typography>
                                    <Typography
                                        color="primary"
                                        variant='h3'
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => {
                                            if (profile?.role === ROLES.admin) {
                                                navigate(`/admin/tasks/${details.task.id}`)
                                            } else {
                                                navigate(`/merchant/tasks/${details.task.id}`)
                                            }
                                        }}
                                    >
                                        {details.task.taskNumber}
                                    </Typography>
                                </Grid>
                            }
                            <Grid item xs={6} sm={3}>
                                <Typography color="text.lighter" variant='body2'>
                                    {`${t("Created At")} `}
                                </Typography>
                                <Typography variant='h3'>
                                    {createdAt && moment(createdAt).format("DD/MM/YYYY - hh:mm a")}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography color="text.lighter" variant='body2'>
                                    {`${t("Updated At")} `}
                                </Typography>
                                <Typography variant='h3'>
                                    {updatedAt && moment(updatedAt).format("DD/MM/YYYY - hh:mm a")}
                                </Typography>
                            </Grid>

                            {/* Full-width Status Track DataGrid */}
                            <Grid item xs={12}>
                                <Typography color="text.lighter" variant='body2' sx={{ mb: 1, mt: 4 }}>
                                    {`${t("Status Track")} `}
                                </Typography>
                                <MapitShipStatusTrackGrid
                                    statusTrack={profile?.role === ROLES.admin ? statusTrackInDetails?.statusTrack || statusTrack : statusTrack}
                                    orderTimelineStatus={orderTimelineStatus}
                                    t={t}
                                    isMapitShip={!!details?.otoIntegration?.mapItShip}
                                    isAdmin={profile?.role === ROLES.admin}
                                    onStatusClick={profile?.role === ROLES.admin ? openStatusHistoryDialogHandler : undefined}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            {/* Original layout: 3-column grid */}
                            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} >
                                <Box>
                                    <Typography color="text.lighter" variant='body2'>
                                        {`${t("Order No.")} `}
                                    </Typography>
                                    <Typography variant='h3'>
                                        {orderNumber}
                                    </Typography>
                                </Box>
                                {!!details?.task?.taskNumber &&
                                    <Box>
                                        <Typography color="text.lighter" variant='body2'>
                                            {`${t("Task No.")} `}
                                        </Typography>
                                        <Typography
                                            color="primary"
                                            variant='h3'
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => {
                                                if (profile?.role === ROLES.admin) {
                                                    navigate(`/admin/tasks/${details.task.id}`)
                                                } else {
                                                    navigate(`/merchant/tasks/${details.task.id}`)
                                                }
                                            }}
                                        >
                                            {details.task.taskNumber}
                                        </Typography>
                                    </Box>
                                }
                                {
                                    !details?.otoIntegration?.mapItShip &&
                                    <Box>
                                        <Typography color="text.lighter" variant='body2'>
                                            {`${t("Zone")} `}
                                        </Typography>
                                        <Typography variant='h3'
                                            sx={{ cursor: profile?.role === ROLES.admin ? "pointer" : "default" }}
                                            onClick={() => profile?.role === ROLES.admin && openZoneHistoryDialogHandler(details?.id)}
                                        >
                                            {zone?.name ?? t("Unlocated")}
                                        </Typography>
                                    </Box>
                                }
                                <Box>
                                    <Typography color="text.lighter" variant='body2'>
                                        {`${t("Created At")} `}
                                    </Typography>
                                    <Typography variant='h3'>
                                        {createdAt && moment(createdAt).format("DD/MM/YYYY - hh:mm a")}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography color="text.lighter" variant='body2'>
                                        {`${t("Updated At")} `}
                                    </Typography>
                                    <Typography variant='h3'>
                                        {updatedAt && moment(updatedAt).format("DD/MM/YYYY - hh:mm a")}
                                    </Typography>
                                    {/* {details.newOrderId && <>
                                        <Typography sx={{ mt: 2 }} color="text.lighter" variant='body2'>
                                            {t("The order was re-order")}
                                        </Typography>
                                        <Typography color="primary" variant='h3' component={Link} to={`/merchant/orders/${details.newOrderId}`}>
                                            {t("Go to the new order")}
                                        </Typography>
                                    </>} */}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} >
                                {!details?.otoIntegration?.mapItShip && <CopyTrackingUrl orderId={details?.id} text={t("Copy Tracking URL")} />}
                                <Typography color="text.lighter" variant='body2'>
                                    {`${t("Status Track")} `}
                                </Typography>
                                {
                                    profile?.role === ROLES.admin ?
                                        map(
                                            statusTrackInDetails?.statusTrack,
                                            statusTrack => (
                                                <Tooltip title={t("Track Status History")} placement="top" key={statusTrack?._id}>
                                                    <Box sx={{ mb: 0.4, cursor: "pointer" }} onClick={() => openStatusHistoryDialogHandler(statusTrack)}>
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            {
                                                                (details?.otoIntegration?.mapItShip && ALL_FAILED_ORDER_STATUS.includes(statusTrack?.status)) &&
                                                                <ShipmentFailReasonDialog failedStatus={statusTrack} />
                                                            }
                                                            <Typography sx={{ mb: -0.6 }} variant="h5">
                                                                {(details?.otoIntegration?.mapItShip && ALL_FAILED_ORDER_STATUS.includes(statusTrack?.status)) ? t("Shipment creation failed") : orderTimelineStatus(statusTrack?.status)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography display='inline-block' variant="h6">{moment(statusTrack?.dateTime).format("DD/MM/YYYY - hh:mm a")}</Typography>
                                                    </Box>
                                                </Tooltip>
                                            )
                                        )
                                        :
                                        map(
                                            statusTrack,
                                            (statusTrack, index) => (
                                                <Box sx={{ mb: 0.4 }} key={statusTrack._id}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        {
                                                            (details?.otoIntegration?.mapItShip && ALL_FAILED_ORDER_STATUS.includes(statusTrack?.status)) &&
                                                            <ShipmentFailReasonDialog failedStatus={statusTrack} />
                                                        }
                                                        <Typography sx={{ mb: -0.6 }} variant="h5">
                                                            {(details?.otoIntegration?.mapItShip && ALL_FAILED_ORDER_STATUS.includes(statusTrack?.status)) ? t("Shipment creation failed") : orderTimelineStatus(statusTrack?.status, index)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography display='inline-block' variant="h6">{moment(statusTrack?.dateTime).format("DD/MM/YYYY - hh:mm a")}</Typography>
                                                </Box>
                                            )
                                        )
                                }
                            </Grid>
                            {
                                !details?.otoIntegration?.mapItShip &&
                                <>
                                    <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <Typography color="text.lighter" variant='body2' mb={1}>
                                            {`${t("Barcode")}`}
                                        </Typography>
                                        {
                                            map(
                                                barCode,
                                                barCode => (
                                                    !!barCode?.image ?
                                                        <Box
                                                            key={barCode._id}
                                                            component="img"
                                                            sx={{
                                                                maxHeight: { xs: 233, md: 167 },
                                                                maxWidth: { xs: 350, md: 250 },
                                                                aspectRatio: 2.35 / 1,
                                                                mb: 2
                                                            }}
                                                            alt={barCode.text}
                                                            src={generateCloudinaryLink(barCode.image)}
                                                        />
                                                        :
                                                        <Typography variant="h5" key={barCode._id}>{barCode.text}</Typography>
                                                )
                                            )
                                        }

                                    </Grid>
                                    {
                                        (!isEmpty(details?.completedOrderImage) || !isEmpty(details?.completedOrderImages)) &&
                                        <Grid item xs={12} md={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Typography variant='body2' mb={1}>
                                                {`${t("Attachment")}`}
                                            </Typography>
                                            {
                                                (!isEmpty(details?.completedOrderImages) && isArray(details?.completedOrderImages)) ?
                                                    <Grid container spacing={2}>
                                                        {
                                                            map(details?.completedOrderImages, (image, index) => (
                                                                <Grid item md={4} sm={6} xs={12} key={index}>
                                                                    <OrderShipmentImage image={image} />
                                                                </Grid>
                                                            ))
                                                        }
                                                    </Grid>
                                                    :
                                                    <Box
                                                        component="img"
                                                        sx={{
                                                            maxHeight: 500,
                                                            // maxWidth: { xs: 350, md: 250 },
                                                            aspectRatio: 1
                                                        }}
                                                        alt={t("Attachment")}
                                                        src={generateCloudinaryLink(details?.completedOrderImage)}
                                                    />
                                            }
                                        </Grid>
                                    }
                                </>
                            }
                        </>
                    )}
                </Grid>
            </CardContent>
            {
                !isEmpty(details) &&
                <Box>
                    {
                        map(
                            details.barCode,
                            (barCode, index) => (<AWB index={index} style={{ display: "none" }} key={`Key_${barCode._id}`} id={`awb${barCode.text}`} order={{ ...details, barCode }} />)
                        )
                    }
                </Box>
            }
        </Card>
    )
};

export default OrderShipmentDetails;